import { zip, zipSync, type AsyncZippable, type Zippable } from 'fflate';

export const PACKAGE_DIRECTORY_SUFFIXES = [
  '.app',
  '.appex',
  '.bundle',
  '.framework',
  '.plugin',
  '.kext',
  '.xpc',
  '.prefpane',
  '.pkg',
  '.mpkg',
  '.pages',
  '.numbers',
  '.key',
  '.rtfd',
] as const;

export const PACKAGE_ZIP_MAX_BYTES = 100 * 1024 * 1024;
export const PACKAGE_ZIP_MAX_FILES = 10_000;
export const LARGE_FOLDER_CONFIRM_BYTES = 500 * 1024 * 1024;
export const LARGE_FOLDER_CONFIRM_FILES = 500;
export const LARGE_FOLDER_CONFIRM_FOLDERS = 100;
export const ZIP_MIME_TYPE = 'application/zip';

export interface UploadDirectoryEntry {
  readonly kind: 'directory';
  readonly relativePath: string;
  readonly displayPath: string;
}

export interface UploadFileEntry {
  readonly kind: 'file';
  readonly file: File;
  readonly folderPath: string;
  readonly uploadName: string;
  readonly displayPath: string;
  readonly pendingUploadId?: string;
}

export interface UploadFailureEntry {
  readonly kind: 'failure';
  readonly displayPath: string;
  readonly folderPath: string;
  readonly uploadName: string;
  readonly reason: PackageUploadFailureReason;
  readonly sizeBytes?: number;
  readonly fileCount?: number;
  readonly pendingUploadId?: string;
}

export type PackageUploadFailureReason =
  | 'package-unreadable'
  | 'package-too-large'
  | 'package-too-many-files'
  | 'package-zip-failed';

export type UploadBatchEntry = UploadDirectoryEntry | UploadFileEntry | UploadFailureEntry;

export interface UploadDropStats {
  readonly totalBytes: number;
  readonly fileCount: number;
  readonly folderCount: number;
  readonly requiresLargeFolderConfirmation: boolean;
}

export interface DragUploadResult {
  readonly entries: UploadBatchEntry[];
  readonly stats: UploadDropStats;
}

export interface DragUploadOptions {
  readonly onArchivePreparing?: (event: ArchivePreparingEvent) => string | undefined;
}

export interface ArchivePreparingEvent {
  readonly displayPath: string;
  readonly uploadName: string;
}

interface WebkitFileSystemEntry {
  readonly isFile: boolean;
  readonly isDirectory: boolean;
  readonly name: string;
}

interface WebkitFileSystemFileEntry extends WebkitFileSystemEntry {
  readonly isFile: true;
  readonly isDirectory: false;
  file(successCallback: (file: File) => void, errorCallback?: (error: DOMException) => void): void;
}

interface WebkitFileSystemDirectoryReader {
  readEntries(
    successCallback: (entries: WebkitFileSystemEntry[]) => void,
    errorCallback?: (error: DOMException) => void,
  ): void;
}

interface WebkitFileSystemDirectoryEntry extends WebkitFileSystemEntry {
  readonly isFile: false;
  readonly isDirectory: true;
  createReader(): WebkitFileSystemDirectoryReader;
}

type DataTransferItemWithEntry = DataTransferItem & {
  getAsEntry?: () => unknown;
  webkitGetAsEntry?: () => unknown;
};

export function normalizeRelativePath(path: string): string {
  return path
    .replace(/\\/g, '/')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join('/');
}

export function folderSegments(path: string): string[] {
  const normalized = normalizeRelativePath(path);
  return normalized ? normalized.split('/') : [];
}

export function flatFileEntries(files: readonly File[]): UploadFileEntry[] {
  return files.map((file) => ({
    kind: 'file',
    file,
    folderPath: '',
    uploadName: file.name,
    displayPath: file.name,
  }));
}

export function shouldConfirmLargeFolderUpload(stats: UploadDropStats): boolean {
  return stats.totalBytes >= LARGE_FOLDER_CONFIRM_BYTES
    || stats.fileCount >= LARGE_FOLDER_CONFIRM_FILES
    || stats.folderCount >= LARGE_FOLDER_CONFIRM_FOLDERS;
}

export function isPackageDirectoryName(name: string): boolean {
  const lookupName = normalizeLookupName(name);
  return PACKAGE_DIRECTORY_SUFFIXES.some((suffix) => lookupName.endsWith(suffix));
}

export function nextAvailableFileName(
  fileName: string,
  unavailableNames: ReadonlySet<string>,
): string {
  const trimmed = fileName.trim();
  const { base, extension } = splitExtension(trimmed || fileName);
  let counter = 1;
  let candidate = `${base} (${counter})${extension}`;
  while (unavailableNames.has(normalizeLookupName(candidate))) {
    counter += 1;
    candidate = `${base} (${counter})${extension}`;
  }
  return candidate;
}

export async function dragUploadEntries(
  dataTransfer: DataTransfer,
  options: DragUploadOptions = {},
): Promise<DragUploadResult | null> {
  const items = Array.from(dataTransfer.items ?? []);
  const entries: WebkitFileSystemEntry[] = [];
  const safariPackageZipFallbacks: File[] = [];
  for (const item of items) {
    const itemWithEntry = item as DataTransferItemWithEntry;
    const entry = itemWithEntry.getAsEntry?.() ?? itemWithEntry.webkitGetAsEntry?.();
    if (!isWebkitEntry(entry)) continue;
    const file = item.kind === 'file' ? item.getAsFile() : null;
    if (isPackageZipFallback(file, entry)) {
      safariPackageZipFallbacks.push(file);
      continue;
    }
    entries.push(entry);
  }

  if (entries.length === 0 && safariPackageZipFallbacks.length === 0) return null;

  const context: WalkContext = {
    entries: [],
    stats: mutableUploadDropStats(),
    onArchivePreparing: options.onArchivePreparing,
  };
  for (const file of safariPackageZipFallbacks) {
    context.entries.push({
      kind: 'file',
      file,
      folderPath: '',
      uploadName: file.name,
      displayPath: file.name,
    });
    context.stats.fileCount += 1;
    context.stats.totalBytes += file.size;
  }
  for (const entry of entries) {
    await walkEntryIfReadable(entry, '', context);
  }
  const stats = snapshotUploadDropStats(context.stats);
  return {
    entries: context.entries,
    stats: {
      ...stats,
      requiresLargeFolderConfirmation: shouldConfirmLargeFolderUpload(stats),
    },
  };
}

export function normalizeLookupName(name: string): string {
  return name.trim().normalize('NFC').toLocaleLowerCase();
}

interface MutableUploadDropStats {
  totalBytes: number;
  fileCount: number;
  folderCount: number;
}

interface WalkContext {
  readonly entries: UploadBatchEntry[];
  readonly stats: MutableUploadDropStats;
  readonly onArchivePreparing?: (event: ArchivePreparingEvent) => string | undefined;
}

interface PackageFileToZip {
  readonly zipPath: string;
  readonly file: File;
}

interface PackageCollectResult {
  readonly files: PackageFileToZip[];
  readonly emptyDirectoryPaths: string[];
  readonly totalBytes: number;
  readonly fileCount: number;
}

class PackageArchiveError extends Error {
  constructor(
    readonly reason: PackageUploadFailureReason,
    readonly sizeBytes?: number,
    readonly fileCount?: number,
  ) {
    super(reason);
  }
}

function splitExtension(fileName: string): { base: string; extension: string } {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === fileName.length - 1) {
    return { base: fileName, extension: '' };
  }
  return {
    base: fileName.slice(0, lastDot),
    extension: fileName.slice(lastDot),
  };
}

function isWebkitEntry(entry: unknown): entry is WebkitFileSystemEntry {
  return typeof entry === 'object'
    && entry !== null
    && 'isFile' in entry
    && 'isDirectory' in entry
    && 'name' in entry
    && typeof (entry as WebkitFileSystemEntry).isFile === 'boolean'
    && typeof (entry as WebkitFileSystemEntry).isDirectory === 'boolean'
    && typeof (entry as WebkitFileSystemEntry).name === 'string';
}

function isPackageZipFallback(file: File | null, entry: WebkitFileSystemEntry): file is File {
  if (!file || !entry.isDirectory) return false;
  if (!hasZipExtension(file.name)) return false;
  if (file.type && normalizeLookupName(file.type) !== normalizeLookupName(ZIP_MIME_TYPE)) return false;
  return isPackageDirectoryName(stripZipExtension(file.name))
    || isPackageDirectoryName(stripZipExtension(entry.name));
}

function hasZipExtension(name: string): boolean {
  return normalizeLookupName(name).endsWith('.zip');
}

function stripZipExtension(name: string): string {
  return name.trim().replace(/\.zip$/i, '');
}

async function walkEntry(
  entry: WebkitFileSystemEntry,
  parentPath: string,
  context: WalkContext,
): Promise<void> {
  const entryName = entry.name.trim();
  if (!entryName) return;
  const relativePath = normalizeRelativePath(parentPath ? `${parentPath}/${entryName}` : entryName);
  if (!relativePath) return;

  if (entry.isDirectory) {
    if (isPackageDirectoryName(entryName)) {
      await walkPackageDirectory(entry as WebkitFileSystemDirectoryEntry, parentPath, relativePath, context);
      return;
    }
    const children = await readAllDirectoryEntries(entry as WebkitFileSystemDirectoryEntry);
    context.entries.push({ kind: 'directory', relativePath, displayPath: relativePath });
    context.stats.folderCount += 1;
    for (const child of children) {
      await walkEntryIfReadable(child, relativePath, context);
    }
    return;
  }

  if (entry.isFile) {
    const file = await readFile(entry as WebkitFileSystemFileEntry);
    const segments = relativePath.split('/');
    const uploadName = segments.at(-1) ?? file.name;
    const folderPath = segments.slice(0, -1).join('/');
    context.entries.push({
      kind: 'file',
      file,
      folderPath,
      uploadName,
      displayPath: relativePath,
    });
    context.stats.fileCount += 1;
    context.stats.totalBytes += file.size;
  }
}

async function walkEntryIfReadable(
  entry: WebkitFileSystemEntry,
  parentPath: string,
  context: WalkContext,
): Promise<void> {
  try {
    await walkEntry(entry, parentPath, context);
  } catch {
    // Browser directory entries can go stale between drop and traversal, especially for macOS
    // package directories. Skip only that entry so the rest of the drop can continue.
  }
}

async function walkPackageDirectory(
  entry: WebkitFileSystemDirectoryEntry,
  parentPath: string,
  relativePath: string,
  context: WalkContext,
): Promise<void> {
  const uploadName = `${entry.name.trim()}.zip`;
  const folderPath = normalizeRelativePath(parentPath);
  const displayPath = `${relativePath}.zip`;
  const pendingUploadId = context.onArchivePreparing?.({ displayPath, uploadName });

  try {
    const file = await archivePackageDirectory(entry, uploadName);
    context.entries.push({
      kind: 'file',
      file,
      folderPath,
      uploadName,
      displayPath,
      pendingUploadId,
    });
    context.stats.fileCount += 1;
    context.stats.totalBytes += file.size;
  } catch (err) {
    const archiveError = err instanceof PackageArchiveError
      ? err
      : new PackageArchiveError('package-zip-failed');
    context.entries.push({
      kind: 'failure',
      displayPath: relativePath,
      folderPath,
      uploadName,
      reason: archiveError.reason,
      sizeBytes: archiveError.sizeBytes,
      fileCount: archiveError.fileCount,
      pendingUploadId,
    });
  }
}

async function archivePackageDirectory(
  entry: WebkitFileSystemDirectoryEntry,
  uploadName: string,
): Promise<File> {
  const packageRoot = entry.name.trim();
  let collected: PackageCollectResult;
  try {
    collected = await collectPackageEntries(entry, packageRoot);
  } catch (err) {
    if (err instanceof PackageArchiveError) throw err;
    throw new PackageArchiveError('package-unreadable');
  }

  let archiveBytes: Uint8Array;
  try {
    archiveBytes = await zipPackage(collected, packageRoot);
  } catch {
    throw new PackageArchiveError('package-zip-failed');
  }

  const archiveBlobPart = new Uint8Array(archiveBytes.length);
  archiveBlobPart.set(archiveBytes);
  return new File([archiveBlobPart], uploadName, { type: ZIP_MIME_TYPE });
}

async function collectPackageEntries(
  entry: WebkitFileSystemDirectoryEntry,
  packageRoot: string,
  relativePath = '',
  totals: MutableUploadDropStats = mutableUploadDropStats(),
): Promise<PackageCollectResult> {
  const children = await readAllDirectoryEntries(entry);
  const files: PackageFileToZip[] = [];
  const emptyDirectoryPaths: string[] = [];

  if (children.length === 0 && relativePath) {
    emptyDirectoryPaths.push(zipPath(packageRoot, relativePath));
  }

  for (const child of children) {
    const childName = child.name.trim();
    if (!childName) continue;
    const childRelativePath = normalizeRelativePath(relativePath ? `${relativePath}/${childName}` : childName);
    if (!childRelativePath) continue;

    if (child.isDirectory) {
      const nested = await collectPackageEntries(
        child as WebkitFileSystemDirectoryEntry,
        packageRoot,
        childRelativePath,
        totals,
      );
      files.push(...nested.files);
      emptyDirectoryPaths.push(...nested.emptyDirectoryPaths);
      continue;
    }

    if (!child.isFile) continue;
    const file = await readFile(child as WebkitFileSystemFileEntry);
    totals.fileCount += 1;
    totals.totalBytes += file.size;
    if (totals.fileCount > PACKAGE_ZIP_MAX_FILES) {
      throw new PackageArchiveError('package-too-many-files', undefined, totals.fileCount);
    }
    if (totals.totalBytes > PACKAGE_ZIP_MAX_BYTES) {
      throw new PackageArchiveError('package-too-large', totals.totalBytes, totals.fileCount);
    }
    files.push({ zipPath: zipPath(packageRoot, childRelativePath), file });
  }

  return {
    files,
    emptyDirectoryPaths,
    totalBytes: totals.totalBytes,
    fileCount: totals.fileCount,
  };
}

async function zipPackage(
  collected: PackageCollectResult,
  packageRoot: string,
): Promise<Uint8Array> {
  const zippable: AsyncZippable = {
    [`${packageRoot}/`]: new Uint8Array(0),
  };
  for (const path of collected.emptyDirectoryPaths) {
    zippable[ensureDirectoryZipPath(path)] = new Uint8Array(0);
  }
  for (const file of collected.files) {
    zippable[file.zipPath] = await readFileBytes(file.file);
  }
  if (typeof Worker === 'undefined') {
    return zipSync(zippable as Zippable, { level: 6 });
  }
  try {
    return await zipAsync(zippable);
  } catch {
    return zipSync(zippable as Zippable, { level: 6 });
  }
}

function zipAsync(zippable: AsyncZippable): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(zippable, { level: 6 }, (err, data) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(data);
    });
  });
}

function zipPath(packageRoot: string, relativePath: string): string {
  return normalizeRelativePath(`${packageRoot}/${relativePath}`);
}

function ensureDirectoryZipPath(path: string): string {
  return path.endsWith('/') ? path : `${path}/`;
}

async function readFileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') {
    return new Uint8Array(await file.arrayBuffer());
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) {
        resolve(new Uint8Array(result));
        return;
      }
      reject(new Error('FileReader did not return an ArrayBuffer'));
    };
    reader.readAsArrayBuffer(file);
  });
}

async function readAllDirectoryEntries(
  entry: WebkitFileSystemDirectoryEntry,
): Promise<WebkitFileSystemEntry[]> {
  const reader = entry.createReader();
  const entries: WebkitFileSystemEntry[] = [];
  while (true) {
    const batch = await new Promise<WebkitFileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) return entries;
    entries.push(...batch);
  }
}

function readFile(entry: WebkitFileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

function mutableUploadDropStats(): MutableUploadDropStats {
  return {
    totalBytes: 0,
    fileCount: 0,
    folderCount: 0,
  };
}

function snapshotUploadDropStats(stats: MutableUploadDropStats): UploadDropStats {
  return {
    totalBytes: stats.totalBytes,
    fileCount: stats.fileCount,
    folderCount: stats.folderCount,
    requiresLargeFolderConfirmation: false,
  };
}
